import React from 'react'
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { useNavigate } from "react-router-dom"
import FreshLeadInterface from "../../interfaces/Cook/FreshLeadInterface"
import { FreshLeadStatus } from "../../enums/FreshLeadStatus"
import { AllCookOnBoardingSubStatus } from "../../enums/AllCookOnboardingSubStatus"
import GroupPermissionFunction from "../../utils/groupPermissionFunction"
import { UserGroupsEnum } from "../../enums/UserGroupsEnum"
import { useAppSelector } from "../../store/hooks"
import { PreviousAssessment } from "../../interfaces/Cook/SkillAssessmentHistoryInterface"

interface AssessmentDataInterface {
    moduleId: number;
    moduleName: string;
    moduleTopics: {
        attempted: boolean;
        completed: boolean;
        maximumScore: number;
        topicProgressId: number;
        percentAttempted: number;
        topicId: number;
        topicName: string;
        totalScore: number;
    }[];
}

interface InputProps {
    cookDetail: FreshLeadInterface;
    moduleData: AssessmentDataInterface;
    hideAction?: boolean;
    previousAssessments?: PreviousAssessment[];
}

const getPrevScore = (prev: PreviousAssessment | undefined, topicId: number): string => {
    if (!prev) return "—"
    const found = prev.assessmentTopicProgess.find(t => t.topic.id === topicId)
    return found?.totalScore != null ? String(Math.round(Number(found.totalScore))) : "—"
}

const TH_SX = { fontSize: "0.7rem", fontWeight: 700, color: "#546E7A", whiteSpace: "nowrap" as const }

const SkillAssessmentRatingTable = ({
    cookDetail,
    moduleData,
    hideAction = false,
    previousAssessments = [],
}: InputProps) => {
    const navigate = useNavigate()
    const userDetails = useAppSelector((state) => state.userDetails.value)
    const groupsArray: string[] = userDetails.adminUser.accessGroups

    const checkDisabled = () => {
        if (GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER, UserGroupsEnum.EXECUTIVE])) {
            return false
        }
        return (
            (cookDetail.onboardingStatus === FreshLeadStatus.SKILL_ASSESSMENT ? false : true) &&
            (cookDetail.onboardingStatus === FreshLeadStatus.ONBOARDING &&
                cookDetail.onboardingSubStatus === AllCookOnBoardingSubStatus.COMPLETE ? false : true)
        )
    }

    // previousAssessments ordered oldest → newest; pad to length 3 so columns always render
    const [prev1, prev2, prev3] = [previousAssessments[0], previousAssessments[1], previousAssessments[2]]

    return (
        <Box sx={{ overflowX: "auto", border: "1px solid rgb(226,230,233)", borderRadius: "8px" }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: "rgb(249,250,251)" }}>
                        <TableCell sx={TH_SX}>Item</TableCell>
                        <TableCell sx={TH_SX}>Status</TableCell>
                        <TableCell sx={TH_SX}>Scores</TableCell>
                        {!hideAction && <TableCell sx={TH_SX}>Action</TableCell>}
                        <TableCell align="center" sx={TH_SX}>Previous Score</TableCell>
                        <TableCell align="center" sx={TH_SX}>Previous Score</TableCell>
                        <TableCell align="center" sx={TH_SX}>Previous Score</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {moduleData.moduleTopics.map((eachTopic) => (
                        <TableRow key={eachTopic.topicId} sx={{ "&:last-child td": { border: 0 } }}>
                            <TableCell sx={{ fontSize: "0.74rem", fontWeight: 600 }}>
                                {eachTopic.topicName}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.74rem" }}>
                                {hideAction
                                    ? (eachTopic.completed
                                        ? "Completed"
                                        : eachTopic.attempted
                                            ? "Attempted"
                                            : "Not Attempted")
                                    : "Active"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.74rem" }}>
                                {eachTopic.totalScore != null ? Math.round(Number(eachTopic.totalScore)) : "—"}
                            </TableCell>
                            {!hideAction && (
                                <TableCell>
                                    <Button
                                        disabled={checkDisabled()}
                                        onClick={() =>
                                            navigate(`/skill-assessment?cookId=${cookDetail.cookId}&topicId=${eachTopic.topicProgressId}`)
                                        }
                                        sx={{
                                            fontSize: "0.75rem",
                                            textTransform: "none",
                                            color: "#1565C0",
                                            fontWeight: 600,
                                            p: 0,
                                            minWidth: 0,
                                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                                        }}
                                    >
                                        {eachTopic.attempted ? "ReTake Assessment" : "Take Assessment"}
                                    </Button>
                                </TableCell>
                            )}
                            <TableCell align="center" sx={{ fontSize: "0.74rem", color: "#546E7A" }}>
                                {getPrevScore(prev1, eachTopic.topicId)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.74rem", color: "#546E7A" }}>
                                {getPrevScore(prev2, eachTopic.topicId)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.74rem", color: "#546E7A" }}>
                                {getPrevScore(prev3, eachTopic.topicId)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    )
}

export default SkillAssessmentRatingTable
